import * as React from 'react'
import { useState } from 'react'
import LinksHeader from './LinksHeader'
import EditingLinkForm from './EditingLinkForm'
import LinkDragWrapper from './LinkDragWrapper'

import styles from './styles.module.css'
import { DEFAULT_COLOR, DEFAULT_CONTRAST_COLOR } from '../../util/index'
import type {
  CustomLink,
  CustomLinksDetails,
} from '~publish/legacy/profile-sidebar/types'

interface EditingLinkItemProps {
  customLinkItem: CustomLink
  onUpdateCustomLinks: (item: CustomLink) => void
  onCancelCustomLinkEdit: (item: CustomLink) => void
}

const EditingLinkItem = ({
  customLinkItem,
  onUpdateCustomLinks,
  onCancelCustomLinkEdit,
}: EditingLinkItemProps): JSX.Element => {
  return (
    <EditingLinkForm
      item={customLinkItem}
      onCancelClick={onCancelCustomLinkEdit}
      onSaveClick={onUpdateCustomLinks}
    />
  )
}

interface CustomLinksProps {
  customLinksDetails: CustomLinksDetails
  onUpdateCustomLinksColor: (colors: {
    color: string
    contrastColor: string
  }) => void
  onDeleteCustomLink: (itemId: string) => void
  maxCustomLinks: number
  onSwapCustomLinks: (from: CustomLink, to: CustomLink) => void
  onSaveNewLinkClick: (item: CustomLink) => void
  onUpdateSingleCustomLink: (item: CustomLink) => void
  hasWriteAccess: boolean
}

const CustomLinks = ({
  customLinksDetails,
  onUpdateCustomLinksColor,
  onDeleteCustomLink,
  maxCustomLinks,
  onSwapCustomLinks,
  onSaveNewLinkClick,
  onUpdateSingleCustomLink,
  hasWriteAccess,
}: CustomLinksProps): JSX.Element => {
  const [editingLinkId, setEditingLinkId] = useState<string | null>(null)
  const [colorButtons, setColorButton] = useState(
    customLinksDetails.color || DEFAULT_COLOR,
  )
  const [textColor, setTextColor] = useState(
    customLinksDetails.contrastColor || DEFAULT_CONTRAST_COLOR,
  )
  const [newLinks, addNewLink] = useState<CustomLink[]>([])

  const onCancelClick = (item: CustomLink): void => {
    addNewLink(newLinks.filter((currentItem) => currentItem.id !== item.id))
  }

  const totalLinks = newLinks.length + (customLinksDetails.links?.length || 0)

  const onAddLinkClick = (): void => {
    addNewLink([
      ...newLinks,
      {
        id: `${Date.now()}`,
        text: '',
        url: '',
      },
    ])
  }
  const { links = [] } = customLinksDetails

  return (
    <div className={styles.myLinksSection}>
      <LinksHeader
        maxCustomLinks={maxCustomLinks}
        onAddLinkClick={onAddLinkClick}
        setColorButton={setColorButton}
        setTextColor={setTextColor}
        colorButtons={colorButtons}
        textColor={textColor}
        onUpdateCustomLinksColor={onUpdateCustomLinksColor}
        addLinkDisabled={totalLinks >= maxCustomLinks}
        hasWriteAccess={hasWriteAccess}
      />
      <div className={styles.myLinksBody}>
        {links?.map((item) => {
          return (
            <React.Fragment key={item.id}>
              {item.id !== editingLinkId && (
                <LinkDragWrapper
                  item={item}
                  textColor={textColor}
                  bgColor={colorButtons}
                  onSwapCustomLinks={onSwapCustomLinks}
                  setEditingLinkId={setEditingLinkId}
                  onDeleteCustomLink={onDeleteCustomLink}
                  hasWriteAccess={hasWriteAccess}
                />
              )}
              {item.id === editingLinkId && (
                <EditingLinkItem
                  key={item.id}
                  customLinkItem={item}
                  onUpdateCustomLinks={(item): void => {
                    setEditingLinkId(null)
                    onUpdateSingleCustomLink(item)
                  }}
                  onCancelCustomLinkEdit={(): void => {
                    setEditingLinkId(null)
                  }}
                />
              )}
            </React.Fragment>
          )
        })}

        {newLinks.map((newLinkItem) => {
          return (
            <EditingLinkForm
              key={newLinkItem.id}
              item={newLinkItem}
              onSaveClick={(item: CustomLink): void => {
                onSaveNewLinkClick(item)
                onCancelClick(item)
              }}
              onCancelClick={onCancelClick}
            />
          )
        })}
      </div>
    </div>
  )
}

export default CustomLinks
